/**
 * NewHotelRequestDialog - modale creazione richiesta con toggle tipo
 * (riparazione / acquisto / fornitore_extra) e campi dinamici.
 */
import React, { useMemo, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Wrench, ShoppingCart, Building2, Loader2 } from 'lucide-react';

const TYPES = [
  { key: 'riparazione', label: 'Riparazione / Segnalazione', icon: Wrench, hint: 'Guasti, danni, infiltrazioni (no costo)' },
  { key: 'acquisto',        label: 'Acquisto',                 icon: ShoppingCart, hint: 'Prodotto con prezzo/link' },
  { key: 'fornitore_extra', label: 'Fornitore Extra',          icon: Building2, hint: 'Chiamata ditta esterna' },
];

const NewHotelRequestDialog = ({ open, onOpenChange, hotels, onCreated }) => {
  const [type, setType] = useState('riparazione');
  const [form, setForm] = useState({
    title: '', description: '', hotel_id: '', priority: 'media', area: '',
    product_link: '', unit_price: '', quantity: 1, supplier_suggested: '',
    supplier_name: '', supplier_phone: '', supplier_email: '', estimated_cost: '',
    scheduled_intervention_date: '',
  });
  const [saving, setSaving] = useState(false);

  const total = useMemo(() => {
    const u = parseFloat(form.unit_price) || 0;
    const q = parseInt(form.quantity, 10) || 1;
    return (u * Math.max(1, q)).toFixed(2);
  }, [form.unit_price, form.quantity]);

  const reset = () => {
    setType('riparazione');
    setForm({
      title: '', description: '', hotel_id: '', priority: 'media', area: '',
      product_link: '', unit_price: '', quantity: 1, supplier_suggested: '',
      supplier_name: '', supplier_phone: '', supplier_email: '', estimated_cost: '',
      scheduled_intervention_date: '',
    });
  };

  const submit = async () => {
    if (!form.title.trim() || form.title.trim().length < 3) return toast.error('Titolo obbligatorio (min 3 caratteri)');
    if (!form.hotel_id) return toast.error('Seleziona un hotel');
    try {
      setSaving(true);
      const payload = {
        type,
        title: form.title.trim(),
        description: form.description.trim(),
        hotel_id: form.hotel_id,
        priority: form.priority,
        area: form.area || undefined,
      };
      if (type === 'acquisto') {
        payload.product_link = form.product_link || undefined;
        payload.unit_price = parseFloat(form.unit_price) || 0;
        payload.quantity = parseInt(form.quantity, 10) || 1;
        payload.supplier_suggested = form.supplier_suggested || undefined;
      } else if (type === 'fornitore_extra') {
        payload.supplier_name = form.supplier_name || undefined;
        payload.supplier_phone = form.supplier_phone || undefined;
        payload.supplier_email = form.supplier_email || undefined;
        payload.estimated_cost = parseFloat(form.estimated_cost) || 0;
        if (form.scheduled_intervention_date) payload.scheduled_intervention_date = new Date(form.scheduled_intervention_date).toISOString();
      }
      await api.post('/hotel-requests', payload);
      toast.success('Richiesta creata');
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore creazione richiesta');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-request-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Nuova Richiesta</DialogTitle>
          <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">Crea una segnalazione, acquisto o chiamata fornitore per un hotel.</DialogDescription>
        </DialogHeader>

        {/* Type selector */}
        <div>
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2 block">Tipo</label>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map(t => {
              const Icon = t.icon;
              const active = type === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  data-testid={`type-${t.key}`}
                  className={`p-3 border-2 rounded-lg text-left transition ${active ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'}`}
                >
                  <Icon className="w-5 h-5 mb-1 text-slate-700 dark:text-slate-300" />
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{t.label}</p>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{t.hint}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Campi comuni */}
        <div className="space-y-3 pt-2">
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Titolo *</label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Infiltrazione camera 314" data-testid="input-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Hotel *</label>
              <Select value={form.hotel_id} onValueChange={(v) => setForm({ ...form, hotel_id: v })}>
                <SelectTrigger data-testid="input-hotel"><SelectValue placeholder="Seleziona hotel" /></SelectTrigger>
                <SelectContent>
                  {(hotels || []).map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Priorità</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="input-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bassa">Bassa</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Area / dettaglio</label>
            <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Es. Camera 314, Piano 2, Hall, Lavanderia" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Descrizione</label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Cos'è successo, dettagli utili, cosa serve" data-testid="input-description" />
          </div>
        </div>

        {/* Campi acquisto */}
        {type === 'acquisto' && (
          <div className="border border-blue-200 dark:border-blue-900/50 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg p-3 space-y-3" data-testid="acquisto-fields">
            <p className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase">Dettagli acquisto</p>
            <Input value={form.product_link} onChange={(e) => setForm({ ...form, product_link: e.target.value })} placeholder="Link prodotto (opzionale)" />
            <div className="grid grid-cols-3 gap-2">
              <Input type="number" step="0.01" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="Prezzo unit. €" />
              <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="Qtà" />
              <Input value={`€ ${total}`} readOnly className="bg-slate-50 dark:bg-slate-800 font-bold" />
            </div>
            <Input value={form.supplier_suggested} onChange={(e) => setForm({ ...form, supplier_suggested: e.target.value })} placeholder="Fornitore consigliato (opzionale)" />
          </div>
        )}

        {/* Campi fornitore extra */}
        {type === 'fornitore_extra' && (
          <div className="border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-900/10 rounded-lg p-3 space-y-3" data-testid="fornitore-fields">
            <p className="text-xs font-bold text-amber-700 dark:text-amber-300 uppercase">Ditta esterna</p>
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Nome ditta" />
              <Input value={form.supplier_phone} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} placeholder="Telefono" />
            </div>
            <Input value={form.supplier_email} onChange={(e) => setForm({ ...form, supplier_email: e.target.value })} placeholder="Email ditta (opzionale)" />
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} placeholder="Preventivo € (opzionale)" />
              <Input type="date" value={form.scheduled_intervention_date} onChange={(e) => setForm({ ...form, scheduled_intervention_date: e.target.value })} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} disabled={saving}>Annulla</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="submit-request">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Crea Richiesta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewHotelRequestDialog;
